import type { ComponentProps, ReactNode } from 'react'
import { InfoAccessoryButton, InfoPopover } from '../shared/PageHelp'
import type { InsightImage, InsightTestResult, InsightsLoadState } from './insightsModel'
import { isSectionFailed, percentOf } from './insightsModel'
import { StackedThumbnails } from './StackedThumbnails'
import { useAnimateWhenVisible } from '../../hooks/useAnimateWhenVisible'
import '../shims/ProgressBarShim.css'

export type InsightIcon = NonNullable<ComponentProps<'s-icon'>['type']>

type HeadingSize = 'large-200' | 'large-300' | 'large-400'

// Polaris body text stops at 14px, so big figures use s-heading with its heading role removed.
export function Figure({ children, size = 'large-300' }: { children: ReactNode; size?: HeadingSize }) {
  return (
    <s-heading fontSize={size} accessibilityRole="presentation">
      {children}
    </s-heading>
  )
}

export function Note({ children }: { children: ReactNode }) {
  return <s-text color="subdued">{children}</s-text>
}

export function Answer({
  figure,
  label,
  sub,
  badge,
  size,
  strongLabel = true,
  leading,
}: {
  figure: ReactNode
  label: ReactNode
  sub?: ReactNode
  badge?: ReactNode
  size?: HeadingSize
  strongLabel?: boolean
  leading?: ReactNode
}) {
  const answer = (
    <s-stack gap="small-100">
      <s-stack direction="inline" gap="small-200" alignItems="center">
        <Figure size={size}>{figure}</Figure>
        {badge}
      </s-stack>
      {strongLabel ? <s-text fontWeight="medium">{label}</s-text> : <s-text>{label}</s-text>}
      {sub ? <s-text color="subdued">{sub}</s-text> : null}
    </s-stack>
  )
  if (!leading) return answer
  return (
    <s-stack direction="inline" gap="small-300" alignItems="center">
      {leading}
      {answer}
    </s-stack>
  )
}

// Every bar is the same ink. The row an answer is about gets dark semibold text, context rows get grey text.
// Bar colour is kept for status only.
export type BarEmphasis = 'focus' | 'context' | 'plain'

export function InsightBar({
  label,
  value,
  max,
  emphasis = 'plain',
  statusTone,
  badge,
  labelContent,
  figure,
  figureSuffix,
  accessibilityLabel,
  formatCount,
}: {
  label: string
  value: number
  max: number
  emphasis?: BarEmphasis
  statusTone?: 'caution' | 'success' | 'critical'
  badge?: string
  labelContent?: ReactNode
  figure?: string
  figureSuffix?: string
  accessibilityLabel?: string
  formatCount: (x: number) => string
}) {
  const shownFigure = figure ?? formatCount(value)
  const suffix = figureSuffix ?? (figure === undefined ? ` · ${percentOf(value, max)}` : '')
  const figureNode =
    emphasis === 'context' ? (
      <s-number color="subdued">{shownFigure}</s-number>
    ) : (
      <s-number fontWeight={emphasis === 'focus' ? 'bold' : 'semibold'} color="base">
        {shownFigure}
      </s-number>
    )
  const defaultLabel =
    emphasis === 'focus' ? (
      <s-text fontWeight="semibold">{label}</s-text>
    ) : emphasis === 'context' ? (
      <s-text color="subdued">{label}</s-text>
    ) : (
      <s-text>{label}</s-text>
    )
  const labelNode = labelContent ?? defaultLabel
  const { ref: progressRef, hasBeenVisible } = useAnimateWhenVisible<HTMLDivElement>()
  return (
    <s-stack gap="small-200">
      <s-grid gridTemplateColumns="1fr auto" gap="small-200" alignItems="center">
        {badge ? (
          <s-stack direction="inline" gap="small-200" alignItems="center">
            {labelNode}
            <s-badge>{badge}</s-badge>
          </s-stack>
        ) : (
          labelNode
        )}
        <s-text color="subdued">
          {figureNode}
          {suffix}
        </s-text>
      </s-grid>
      <div
        ref={progressRef}
        className={`tf-progress-fill${hasBeenVisible ? ' tf-progress-fill--visible' : ''}`}
        style={{ inlineSize: '100%' }}
      >
        <s-progress
          tone={statusTone}
          value={value}
          max={max}
          accessibilityLabel={
            accessibilityLabel ?? `${label}, ${formatCount(value)} of ${formatCount(max)}, ${percentOf(value, max)}`
          }
        />
      </div>
    </s-stack>
  )
}

export function NumericCell({ children }: { children: ReactNode }) {
  return (
    <s-table-cell>
      <s-stack direction="inline" justifyContent="end" alignItems="center" gap="small-200">
        {children}
      </s-stack>
    </s-table-cell>
  )
}

export function VerdictBadge({ result }: { result: InsightTestResult }) {
  return result.verdict === 'win' ? (
    <s-badge tone="success">Clear winner</s-badge>
  ) : (
    <s-badge>Too close to call</s-badge>
  )
}

export function BarList({ children }: { children: ReactNode }) {
  return <s-stack gap="base">{children}</s-stack>
}

// A labelled group inside a bar list: a small heading with its subtotal, then its rows.
export function BarGroup({ title, total, children }: { title: string; total?: string; children: ReactNode }) {
  return (
    <s-stack gap="small">
      <s-grid gridTemplateColumns="1fr auto" gap="small-200" alignItems="center">
        <s-text fontWeight="semibold">{title}</s-text>
        {total ? <s-text fontWeight="semibold">{total}</s-text> : null}
      </s-grid>
      <BarList>{children}</BarList>
    </s-stack>
  )
}

export function Divided({ items, gap = 'base' }: { items: ReactNode[]; gap?: 'base' | 'small' }) {
  return (
    <s-stack gap={gap}>
      {items.flatMap((item, index) => (index ? [<s-divider key={`divider-${index}`} />, item] : [item]))}
    </s-stack>
  )
}

export function TwoColumns({ first, second }: { first: ReactNode; second: ReactNode }) {
  return (
    <s-grid gridTemplateColumns="@container (inline-size <= 700px) 1fr, 1fr 1fr" gap="base" alignItems="start">
      {first}
      {second}
    </s-grid>
  )
}

export type ThumbnailSize = 'small-200' | 'small'

// A set or bundle shows the products inside it.
export function ProductPictures({
  image,
  size = 'small-200',
}: {
  image: InsightImage & { contents?: readonly InsightImage[] }
  size?: ThumbnailSize
}) {
  if (image.contents) {
    return <StackedThumbnails images={image.contents} size={size} />
  }
  return <s-thumbnail size={size} src={image.imageUrl} alt={`Photo of ${image.name}`} />
}

export function ProductLabel({
  image,
  onOpen,
  extra,
}: {
  image: InsightImage & { contents?: readonly InsightImage[] }
  onOpen: () => void
  extra?: ReactNode
}) {
  return (
    <s-stack direction="inline" gap="small" alignItems="center">
      <ProductPictures image={image} />
      <s-stack direction="inline" gap="small-200" alignItems="center">
        <s-link onClick={onOpen}>{image.name}</s-link>
        {image.contents ? <Note>· set of {image.contents.length}</Note> : null}
        {extra}
      </s-stack>
    </s-stack>
  )
}

export type InsightSectionContext = {
  pageId: string
  loadState: InsightsLoadState
  onRetrySection: (sectionKey: string) => void
}

export function sectionAnchorId(pageId: string, key: string): string {
  return `${pageId}-section-${key}`
}

export function scrollToSection(pageId: string, key: string): void {
  document.getElementById(sectionAnchorId(pageId, key))?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

// Every panel has an info button in its header that opens "Why this matters" and "Where the numbers come from".
export function InsightSection({
  pageId,
  sectionKey,
  heading,
  icon,
  why,
  how,
  loadState,
  onRetrySection,
  headerSlots,
  children,
}: {
  pageId: string
  sectionKey: string
  heading: string
  icon: InsightIcon
  why: string
  how: string
  loadState: InsightsLoadState
  onRetrySection: (sectionKey: string) => void
  headerSlots?: ReactNode
  children: ReactNode
}) {
  const popoverId = `${pageId}-info-${sectionKey}`
  const failed = isSectionFailed(loadState, sectionKey)
  const loading = loadState.status === 'loading'
  let body = children
  if (loading) {
    body = (
      <s-stack alignItems="center" paddingBlock="large">
        <s-spinner accessibilityLabel={`Loading ${heading.toLowerCase()}`} />
      </s-stack>
    )
  } else if (failed) {
    body = (
      <s-banner tone="critical" heading="This part didn't load">
        Everything else on the page is up to date.
        <s-button slot="secondary-actions" variant="secondary" onClick={() => onRetrySection(sectionKey)}>
          Try again
        </s-button>
      </s-banner>
    )
  }
  return (
    <div>
      <div id={sectionAnchorId(pageId, sectionKey)} />
      <s-section heading={heading}>
        <s-icon slot="graphic" type={icon} />
        <InfoAccessoryButton id={popoverId} heading={heading} />
        {loading || failed ? null : headerSlots}
        {body}
      </s-section>
      <InfoPopover id={popoverId} why={why} how={how} />
    </div>
  )
}
